import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';

export default function ReceiptGenerator({ onClose, editData = null, onRefresh }) {
  const [formData, setFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    billingAddress: '',
    shippingAddress: '',
    customerGst: '',
    isOriginal: true,
    items: [
      { description: '', hsn: '999293', quantity: 1, rate: 0, discount: 0 }
    ],
    paymentMode: 'UPI',
    date: new Date().toISOString().split('T')[0],
    receiptNumber: `RCPT-${Date.now().toString().slice(-6)}`,
    termsAndConditions: `1. Fees is not Refundable in any case nor it is transferable or adjustable with any other admission.\n2. All disputes are subject to Chandigarh jurisdiction only`,
    taxType: 'CGST_SGST',
    taxRate: 18,
    isTaxInclusive: false,
    placeOfSupply: ''
  });

  useEffect(() => {
    if (editData) {
      setFormData({
        ...editData,
        date: editData.receiptDate || new Date().toISOString().split('T')[0]
      });
      setSyncShipping(false);
    }
  }, [editData]);

  const [generating, setGenerating] = useState(false);
  const [syncShipping, setSyncShipping] = useState(!editData);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const updated = { ...prev, [name]: val };
      if (name === 'billingAddress' && syncShipping) {
        updated.shippingAddress = value;
      }
      return updated;
    });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', hsn: '999293', quantity: 1, rate: 0, discount: 0 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => {
      const taxable = (item.quantity * item.rate) - item.discount;
      const taxRateValue = parseFloat(formData.taxRate) || 0;
      if (formData.isTaxInclusive) {
        return sum + taxable;
      }
      return sum + taxable * (1 + taxRateValue / 100);
    }, 0).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGenerating(true);
    const userStr = localStorage.getItem('user');
    let token = '';
    let tenantId = 'PENSENSE_DEFAULT';
    
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        token = user.token || '';
        tenantId = user.tenantEmail || 'PENSENSE_DEFAULT';
      } catch (e) {}
    }

    try {
      if (editData) {
        await axios.put(`${API_BASE_URL}/api/payments/standalone-receipts/${editData.id}`, formData, {
          headers: {
            'X-Tenant-ID': tenantId,
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });
        toast.success("Receipt updated successfully!");
        if (onRefresh) onRefresh();
        onClose();
      } else {
        const response = await axios.post(`${API_BASE_URL}/api/payments/generate-standalone-receipt`, formData, {
          responseType: 'blob',
          headers: {
            'X-Tenant-ID': tenantId,
            'Authorization': token ? `Bearer ${token}` : ''
          }
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Receipt_${formData.clientName.replace(/\s+/g, '_')}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success("Receipt generated and downloaded!");
        if (onRefresh) onRefresh();
        onClose();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to process receipt. Please check your connection.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-slate-900 border border-slate-800 rounded-[2.5rem] w-full max-w-5xl shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/10 blur-[120px] rounded-full -mr-48 -mt-48"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/10 blur-[120px] rounded-full -ml-48 -mb-48"></div>
        
        <div className="p-8 border-b border-slate-800 flex justify-between items-center relative z-10 shrink-0">
          <div>
            <h2 className="text-3xl font-black text-white tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
              {editData ? 'Modify Quick Receipt' : 'Quick Receipt Generator'}
            </h2>
            <p className="text-slate-400 mt-1 text-sm font-medium">Issue professional multi-item GST receipts instantly.</p>
          </div>
          <div className="flex items-center gap-4">
             {!editData && (
               <div className="flex items-center gap-2 bg-slate-950/50 border border-slate-800 rounded-xl px-4 py-2">
                  <input 
                    type="checkbox" name="isOriginal" checked={formData.isOriginal} onChange={handleChange}
                    className="w-4 h-4 rounded border-slate-800 text-blue-500 focus:ring-blue-500 bg-slate-900/50"
                  />
                  <span className="text-xs font-bold text-slate-300 uppercase tracking-widest">Mark as Original</span>
               </div>
             )}
             <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-full text-slate-400 transition-colors">
               <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
             </button>
          </div>
        </div>

        <div className="p-8 relative z-10 overflow-y-auto custom-scrollbar flex-1">
          <form onSubmit={handleSubmit} className="space-y-8">
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Client Full Name</label>
                <input 
                  type="text" name="clientName" required value={formData.clientName} onChange={handleChange}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white focus:outline-none focus:border-blue-500 transition-all shadow-inner"
                  placeholder="e.g. John Doe"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Email or Phone</label>
                <input 
                  type="text" name="clientEmail" value={formData.clientEmail} onChange={handleChange}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white focus:outline-none focus:border-blue-500 transition-all shadow-inner"
                  placeholder="john@example.com"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Customer GSTIN</label>
                <input 
                  type="text" name="customerGst" value={formData.customerGst} onChange={handleChange}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white focus:outline-none focus:border-blue-500 transition-all shadow-inner font-mono"
                  placeholder="27AAAAA0000A1Z5"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="flex justify-between items-center ml-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Billing Address</label>
                </div>
                <textarea 
                  name="billingAddress" value={formData.billingAddress} onChange={handleChange}
                  rows="3"
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white focus:outline-none focus:border-blue-500 transition-all shadow-inner resize-none text-sm"
                  placeholder="Full billing address..."
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center ml-1">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Shipping Address</label>
                  {!editData && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" checked={syncShipping} onChange={(e) => {
                          setSyncShipping(e.target.checked);
                          if (e.target.checked) setFormData({ ...formData, shippingAddress: formData.billingAddress });
                        }}
                        className="w-3 h-3 rounded border-slate-800 text-blue-500 focus:ring-blue-500 bg-slate-900/50"
                      />
                      <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Same as Billing</span>
                    </label>
                  )}
                </div>
                <textarea 
                  name="shippingAddress" value={formData.shippingAddress} onChange={handleChange}
                  rows="3" disabled={syncShipping}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white focus:outline-none focus:border-blue-500 transition-all shadow-inner resize-none text-sm disabled:opacity-50"
                  placeholder="Full shipping address..."
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-400 ml-1">Line Items & Particulars</h3>
                <button 
                  type="button" onClick={addItem}
                  className="text-[10px] font-black text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1.5 bg-blue-500/10 px-3 py-1.5 rounded-lg border border-blue-500/20"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                  Add Another Item
                </button>
              </div>

              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-3 p-4 bg-slate-950/30 border border-slate-800/50 rounded-2xl animate-in slide-in-from-right-2 duration-300">
                    <div className="col-span-12 md:col-span-4 space-y-1">
                      <label className="text-[9px] font-bold uppercase text-slate-600 ml-1">Item</label>
                      <input 
                        type="text" value={item.description} onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                        className="w-full bg-slate-900/50 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500"
                        placeholder="Particulars..."
                      />
                    </div>
                    <div className="col-span-6 md:col-span-2 space-y-1">
                      <label className="text-[9px] font-bold uppercase text-slate-600 ml-1">HSN/SAC</label>
                      <input 
                        type="text" value={item.hsn} onChange={(e) => handleItemChange(index, 'hsn', e.target.value)}
                        className="w-full bg-slate-900/50 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 font-mono"
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1 space-y-1">
                      <label className="text-[9px] font-bold uppercase text-slate-600 ml-1">Qty</label>
                      <input 
                        type="number" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                        className="w-full bg-slate-900/50 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 text-center"
                      />
                    </div>
                    <div className="col-span-6 md:col-span-2 space-y-1">
                      <label className="text-[9px] font-bold uppercase text-slate-600 ml-1">Rate</label>
                      <input 
                        type="number" value={item.rate} 
                        onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                        onFocus={(e) => { if(item.rate === 0 || item.rate === '0') handleItemChange(index, 'rate', ''); }}
                        onBlur={(e) => { if(item.rate === '' || item.rate === null) handleItemChange(index, 'rate', 0); }}
                        className="w-full bg-slate-900/50 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 font-mono"
                      />
                    </div>
                    <div className="col-span-6 md:col-span-2 space-y-1">
                      <label className="text-[9px] font-bold uppercase text-slate-600 ml-1">Discount</label>
                      <input 
                        type="number" value={item.discount} 
                        onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                        onFocus={(e) => { if(item.discount === 0 || item.discount === '0') handleItemChange(index, 'discount', ''); }}
                        onBlur={(e) => { if(item.discount === '' || item.discount === null) handleItemChange(index, 'discount', 0); }}
                        className="w-full bg-slate-900/50 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500 font-mono"
                      />
                    </div>
                    <div className="col-span-3 md:col-span-1 flex items-end pb-1 justify-center">
                      <button 
                        type="button" onClick={() => removeItem(index)}
                        disabled={formData.items.length === 1}
                        className="p-2.5 text-slate-600 hover:text-rose-500 transition-colors disabled:opacity-0"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-4 border-t border-slate-800/50">
              <div className="md:col-span-1 space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Invoice Number</label>
                <input 
                  type="text" name="receiptNumber" value={formData.receiptNumber} onChange={handleChange}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white text-sm focus:outline-none focus:border-blue-500 font-mono"
                />
              </div>
              <div className="md:col-span-1 space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Date</label>
                <input 
                  type="date" name="date" value={formData.date} onChange={handleChange}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="md:col-span-1 space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Payment Mode</label>
                <select 
                  name="paymentMode" value={formData.paymentMode} onChange={handleChange}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white text-sm focus:outline-none focus:border-blue-500 appearance-none cursor-pointer"
                >
                  <option value="UPI">UPI / GPay / PhonePe</option>
                  <option value="CASH">Cash Payment</option>
                  <option value="BANK_TRANSFER">Bank Transfer / NEFT</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
              </div>
              <div className="md:col-span-1 space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Place of Supply</label>
                <input 
                  type="text" name="placeOfSupply" value={formData.placeOfSupply} onChange={handleChange}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white text-sm focus:outline-none focus:border-blue-500"
                  placeholder="e.g. Haryana"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-4 border-t border-slate-800/50">
              <div className="md:col-span-1 space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Tax Type</label>
                <select 
                  name="taxType" value={formData.taxType} onChange={handleChange}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white text-sm focus:outline-none focus:border-blue-500 appearance-none cursor-pointer"
                >
                  <option value="CGST_SGST">CGST + SGST/UTGST</option>
                  <option value="IGST">IGST (Integrated)</option>
                </select>
              </div>
              <div className="md:col-span-1 space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Tax Rate (%)</label>
                <select 
                  name="taxRate" value={formData.taxRate} onChange={handleChange}
                  className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white text-sm focus:outline-none focus:border-blue-500 appearance-none cursor-pointer"
                >
                  <option value="0">0% (Exempt)</option>
                  <option value="5">5%</option>
                  <option value="12">12%</option>
                  <option value="18">18% (Standard)</option>
                  <option value="28">28%</option>
                </select>
              </div>
              <div className="md:col-span-1 flex flex-col justify-end">
                <div className="flex items-center gap-3 bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 h-[60px]">
                   <input 
                     type="checkbox" name="isTaxInclusive" checked={formData.isTaxInclusive} onChange={handleChange}
                     id="isTaxInclusive"
                     className="w-5 h-5 rounded border-slate-800 text-blue-500 focus:ring-blue-500 bg-slate-900/50"
                   />
                   <label htmlFor="isTaxInclusive" className="text-xs font-bold text-slate-300 uppercase tracking-widest cursor-pointer select-none">Amount is Inclusive of Tax</label>
                </div>
              </div>
              <div className="md:col-span-1 flex flex-col justify-end items-end">
                <div className="text-right p-6 bg-blue-500/5 border border-blue-500/10 rounded-[2rem] w-full">
                   <p className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-400 mb-1">Total (Incl. {formData.taxRate}% Tax)</p>
                   <p className="text-4xl font-black text-white font-mono tracking-tighter">₹{calculateTotal()}</p>
                </div>
              </div>
            </div>

            <div className="space-y-2 pt-4 border-t border-slate-800/50">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Terms and Conditions</label>
              <textarea 
                name="termsAndConditions" value={formData.termsAndConditions} onChange={handleChange}
                rows="3"
                className="w-full bg-slate-950/50 border border-slate-800 rounded-2xl px-5 py-4 text-white focus:outline-none focus:border-blue-500 transition-all shadow-inner resize-none text-xs leading-relaxed"
                placeholder="Enter terms and conditions..."
              />
            </div>
          </form>
        </div>

        <div className="p-8 bg-slate-950/50 border-t border-slate-800 flex gap-4 shrink-0 relative z-10">
          <button 
            type="button" onClick={onClose}
            className="px-8 py-4 rounded-2xl border border-slate-800 text-slate-300 font-bold hover:bg-slate-800 transition-all"
          >
            Cancel
          </button>
          <button 
            type="button" onClick={handleSubmit} disabled={generating || formData.items.some(i => !i.description)}
            className="flex-1 py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 text-white font-black uppercase tracking-widest shadow-xl shadow-blue-500/20 hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50"
          >
            {generating ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                {editData ? 'Updating...' : 'Processing...'}
              </span>
            ) : (editData ? 'Update Receipt' : 'Generate Professional Receipt')}
          </button>
        </div>
      </div>
    </div>
  );
}
